import { format as formatDate } from "date-fns";
import { Immutable } from "immer";
import React from "react";
import ReactTooltip from "react-tooltip";
import { t } from "ttag";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import { useAppSelector } from "../../../store/reducers";
import { IRootState } from "../../../store/reducers.interfaces";
import { PredictedDeliveryLoader } from "../PredictedDeliveryLoader";
import {
    PredictedDeliveryDateDisplayType,
    PredictedDeliveryDateStatus,
} from "../constants";

import styles from "./index.module.scss";

const shouldShowFallback = (
    type: PredictedDeliveryDateDisplayType,
    status: PredictedDeliveryDateStatus,
): boolean => {
    return (
        type === PredictedDeliveryDateDisplayType.DISABLED ||
        status === PredictedDeliveryDateStatus.INITIAL ||
        status === PredictedDeliveryDateStatus.ERROR ||
        status === PredictedDeliveryDateStatus.LOADING
    );
};

export const PredictedDeliveryHelpText = (
    props: Immutable<{
        type: PredictedDeliveryDateDisplayType;
        additionalHelpText: string;
    }>,
): React.ReactNode => {
    const prediction = useAppSelector(
        (rootState: IRootState) =>
            rootState.checkout.data.predicted_delivery_date,
    );

    if (!prediction || shouldShowFallback(props.type, prediction.status)) {
        return <span>{props.additionalHelpText}</span>;
    }

    // Type narrowing: if we passed shouldShowFallback, we know prediction has data
    if (prediction.status !== PredictedDeliveryDateStatus.LOADED) {
        return <span>{props.additionalHelpText}</span>;
    }

    const tipId = "predicted-delivery-date-help-text__tooltip";
    const displayDate =
        props.type === PredictedDeliveryDateDisplayType.SHOW_AGGRESSIVE
            ? prediction.data.date_aggressive
            : prediction.data.date_conservative;
    const postcode = prediction.data.postcode;

    return (
        <>
            <PredictedDeliveryLoader />
            {richT(
                t`Arrives to <Location /> as soon as <Date />. <HelpText />`,
                {
                    Location: () => (
                        <span
                            aria-describedby={tipId}
                            className={styles.zip}
                            data-for={tipId}
                            data-tip={true}
                            tabIndex={0}
                        >
                            {postcode}
                        </span>
                    ),
                    Date: () => (
                        <span>
                            {/* TODO(i18n): Date format "MMMM do" is English-specific and doesn't support localized date formatting. Consider using date-fns locale support or a localization-aware date formatter. */}
                            {formatDate(displayDate, "MMMM do")}
                        </span>
                    ),
                    HelpText: () => <span>{props.additionalHelpText}</span>,
                },
            )}
            <ReactTooltip
                aria-label={t`Question about your order?`}
                class={styles.tooltip}
                event={"mouseover focus"}
                eventOff="scroll mousewheel mouseleave mouseout blur"
                id={tipId}
                uuid={tipId}
                place="top"
                role="tooltip"
                isCapture={true}
            >
                {/* TODO(i18n): Wrap in t tagged template: {t`Need to change your delivery ZIP Code? Please return to the "Shipping" section to update your address.`} */}
                <span>
                    Need to change your delivery ZIP Code? Please return to the
                    &quot;Shipping&quot; section to update your address.
                </span>
            </ReactTooltip>
        </>
    );
};
